import {
    LitElement,
    TemplateResult
} from 'lit';
import { customElement, property, state} from 'lit/decorators.js';
import { GRAPH_TYPES } from '../../api/reporting';
import { AGGREGATES_KEY, COLUMN_CHART_MEASUREMENTS } from '../../api/graphDataApi';
import { getCachedData, MEASUREMENT_TO_FUNC } from '../../api/dataManagementApi';
import { EVENTS } from '../../api/ui';

@customElement('berner-chart-base')
export class BernerChartBase extends LitElement {
    @property({ type: String, attribute: 'device-ids' })
    protected deviceIds = '';

    @property({ type: String, attribute: 'measurement-one' })
    protected measurementOne = '';

    @property({ type: String, attribute: 'measurement-two' })
    protected measurementTwo = '';

    @property({ type: String, attribute: 'plot-averages' })
    protected plotAverages = 'false';

    @property({ type: String, attribute: 'aggregate' })
    protected aggregate = 'false';

    @property({ type: String, attribute: 'sum' })
    protected sum = 'false';

    @property({ type: String, attribute: 'label-one' })
    protected labelOne = '';

    @property({ type: String, attribute: 'label-two' })
    protected labelTwo = '';

    @property({ type: String, attribute: 'range' })
    protected range = '';

    @property({ type: String, attribute: 'minimal-ui' })
    protected minimalUI = 'false';

    @property({ type: String, attribute: 'show-title' })
    protected showTitle = 'true';

    @property({ type: String, attribute: 'title-position' })
    protected titlePosition = '';

    @property({ type: String, attribute: 'ratio' })
    protected ratio = '';

    @property({ type: String, attribute: 'site-id' })
    protected siteId = '';

    @state()
    protected _error = '';

    @state()
    protected _hasData = false;
    protected _data: Map<string, Map<string, GraphData[]>> = new Map();
    protected _chart: Highcharts.Chart;
    private _emittedRenderedEvent = false;

    get useMinimalUI(): boolean { return this.minimalUI === 'true';}
    get shouldShowTitle(): boolean { return this.showTitle === 'true';}
    get shouldPlotAverages(): boolean { return this.plotAverages === 'true';}
    get shouldAggregate(): boolean { return this.aggregate === 'true';}
    get shouldSum(): boolean { return this.sum === 'true';}

    public connectedCallback(): void {
        window.addEventListener(EVENTS.CHANGE_TEMP_UNIT, this.onTempUnitChanged);
        super.connectedCallback();
    }

    public disconnectedCallback(): void {
        window.removeEventListener(EVENTS.CHANGE_TEMP_UNIT, this.onTempUnitChanged);
        super.disconnectedCallback();
    }

    updated(changed: Map<string, string>): void {
        // changes that require a request to the back-end
        if (
            changed.has('plotAverages') ||
            changed.has('deviceIds') ||
            changed.has('range') ||
            changed.has('measurementOne') ||
            changed.has('measurementTwo') ||
            changed.has('aggregate') ||
            changed.has('labelOne') ||
            changed.has('labelTwo')
        ) {
            this.refreshGraph();
        }
    }

    getGraphType = (measurement: string): string => {
        const graphType = COLUMN_CHART_MEASUREMENTS.includes(measurement) ? 
        GRAPH_TYPES.COLUMN : GRAPH_TYPES.AREASPLINE;
        return graphType;
    }

    onTempUnitChanged = (): void => {
        this.refreshGraph();
    }

    async refreshGraph(): Promise<void> {
        this._error = '';
        this._hasData = false;

        const measurements: string[] = [];
        if (this.measurementOne.trim().length) {
            measurements.push(this.measurementOne);
        }

        if (this.measurementTwo.trim().length) {
            measurements.push(this.measurementTwo);
        }

        let rangeData = new Map<string, GraphData[]>();
        const cachedData = await getCachedData(
            this.range,
            this.deviceIds.indexOf(',') ? this.deviceIds.split(',') : [this.deviceIds],
            [this.measurementOne, this.measurementTwo],
            this.shouldAggregate,
            false,
            this.siteId
        );
        rangeData = cachedData.get(this.shouldAggregate ? AGGREGATES_KEY : this.deviceIds.indexOf(',')
        ? this.deviceIds.split(',')[0]
        : this.deviceIds);
        this._data.set(this.range, rangeData);
        const aggregateFunc = MEASUREMENT_TO_FUNC.get(measurements[0]);
        const measurementName = this.shouldAggregate ? aggregateFunc + '(' + measurements[0] + ')' : measurements[0];
        const deviceData = rangeData?.get(measurementName);

        if (deviceData?.length) {
            this._hasData = true;
            // work around, make sure the dom is updated first
            setTimeout(() => {
                this.drawGraph();
                const credits = this.shadowRoot.querySelector(
                    '.highcharts-credits'
                );
                credits?.classList.add('hidden');

                this._chart.reflow(); // workaround for chart overflows parent div
                if (!this._emittedRenderedEvent) {
                    this._emittedRenderedEvent = true;
                    const event = new CustomEvent(EVENTS.CHART_RENDERED, {
                        detail: { chart: this.id },
                    });
                    window.dispatchEvent(event);
                }
            }, 1500);
        } else {
            this._error = 'no data for ' + this.measurementOne;
            console.log(this._error);
            const canvas = this.shadowRoot.getElementById('canvas');
            canvas.innerHTML = '';
        }
    }

    drawGraph = (): void => {
        throw new Error('drawGraph should be implemented in a derived class');
    };

    render(): TemplateResult {
        throw new Error('render should be implemented in a derived class');
    }
}
